import { Button } from '@/components/ui/button.js';
import { Fragment } from 'react';
import { cn } from '@/lib/tailwind-utils.js';
import {
  AssessmentFormat,
  CoachPortalInAppAssessment,
  CoachPortalSession,
  calculateReadingWPM,
  isProgressMonitorFormat,
  studentYearOption,
} from '@airreading/common/data';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Flag,
  Lightbulb,
  Minus,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  X,
} from 'lucide-react';
import { ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import {
  AssessmentPhase,
  canMarkInAppAssessmentError,
  canSelectInAppAssessmentLastWord,
  formatCountdown,
  InAppAssessmentStudent,
  InAppStudentStatus,
  inAppProgressMonitorPart,
  inAppStudentStatus,
  inAppAssessmentServerStats,
  isInAppAssessmentAttemptActive,
  isInAppAssessmentResultSubmitted,
  sessionHasIncompleteInAppAssessment,
  shouldShowInAppRetell,
  passageLineBreaksBefore,
  tokenizePassage,
  wordListSwitchBlockName,
} from './in-app-assessment-utils.js';
import { AssessmentWindowProvider, useAssessmentWindow } from './in-app-assessment-window-context.js';
import { useInAppAssessmentRunner } from './use-in-app-assessment-runner.js';
import { useInAppAssessmentRedo } from './use-in-app-assessment-redo.js';
import { useInAppAssessmentSubmit } from './use-in-app-assessment-submit.js';

type TimeUpClickMode = 'markErrors' | 'pickLastWord';

/**
 * Floating window the coach runs assessments from.
 *
 * Deliberately non-modal and undimmed: the coach is on a video call behind this and must stay able to
 * use the rest of the portal. Attempt state lives here in the opener document. When Picture-in-Picture
 * is active the shell is rendered into the PiP window with its own React root so clicks work, while this
 * component stays mounted so the timer and marks are not reset.
 */
export function InAppAssessmentWindow({
  session,
  open,
  onClose,
  pipWindow,
  pictureInPictureSupported,
  onEnterPictureInPicture,
}: {
  session: CoachPortalSession;
  open: boolean;
  onClose: () => void;
  pipWindow: Window | null;
  pictureInPictureSupported: boolean;
  onEnterPictureInPicture: () => void;
}) {
  const pipRootRef = useRef<Root | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const closePausedTimerRef = useRef(false);
  const [wordListSwitchPrompt, setWordListSwitchPrompt] = useState<{
    studentName: string;
    targetStudentId: string;
  } | null>(null);
  const [redoPromptOpen, setRedoPromptOpen] = useState(false);
  const [redoReason, setRedoReason] = useState('');
  const [clearedResultIds, setClearedResultIds] = useState<string[]>([]);
  const [timeUpClickMode, setTimeUpClickMode] = useState<TimeUpClickMode>('pickLastWord');
  const [timeUpClickModeResultId, setTimeUpClickModeResultId] = useState<string | null>(null);
  const isPictureInPicture = pipWindow != null && !pipWindow.closed;

  const activeSession = useMemo(() => {
    if (clearedResultIds.length === 0) {
      return session;
    }
    const cleared = new Set(clearedResultIds);
    return {
      ...session,
      inAppAssessments: session.inAppAssessments?.map((assessment) =>
        cleared.has(assessment.assessmentResultId) ? { ...assessment, assessmentFinished: null } : assessment,
      ),
    };
  }, [clearedResultIds, session]);

  const groups = useMemo(() => timedContentGroups(activeSession), [activeSession]);
  const needsPicker = groups.length > 1;
  const [selectedKind, setSelectedKind] = useState<TimedContentKind | null>(
    needsPicker ? null : groups[0]?.kind ?? null,
  );
  const students = useMemo(
    () => (selectedKind ? studentsForKind(activeSession, selectedKind) : []),
    [activeSession, selectedKind],
  );
  const [selectedAssessmentResultId, setSelectedAssessmentResultId] = useState<string | null>(() =>
    firstAssessmentResultId(students),
  );

  const selectedStudent = studentForResultId(students, selectedAssessmentResultId);
  const selectedAssessment = selectedStudent?.assessments.find(
    (assessment) => assessment.assessmentResultId === selectedAssessmentResultId,
  );
  const runner = useInAppAssessmentRunner({
    selectedAssessmentResultId,
  });
  const timeUpResultId = runner.selectedState.phase === 'timeUp' ? selectedAssessmentResultId : null;
  if (timeUpResultId === null) {
    if (timeUpClickModeResultId !== null) {
      setTimeUpClickModeResultId(null);
    }
  } else if (timeUpResultId !== timeUpClickModeResultId) {
    setTimeUpClickModeResultId(timeUpResultId);
    setTimeUpClickMode('pickLastWord');
  }
  const submitAssessment = useInAppAssessmentSubmit({
    selectedAssessmentResultId,
    words: tokensForAssessment(selectedAssessment),
    selectedState: runner.selectedState,
    markSubmitting: runner.markSubmitting,
    markSubmitted: runner.markSubmitted,
    revertPhase: runner.revertPhase,
  });
  const clearResult = runner.clearResult;
  const onRedone = useCallback(
    (assessmentResultId: string) => {
      clearResult(assessmentResultId);
      setClearedResultIds((current) =>
        current.includes(assessmentResultId) ? current : [...current, assessmentResultId],
      );
      setRedoPromptOpen(false);
      setRedoReason('');
    },
    [clearResult],
  );
  const { redo, redoPending } = useInAppAssessmentRedo({ onRedone });
  const navigationLocked = selectedKind != null && isInAppAssessmentAttemptActive(runner.selectedState.phase);
  const headerTitle = selectedAssessment
    ? `${selectedAssessment.assessmentDisplayName} ${selectedAssessment.assessmentContentName}`
    : 'Select assessment';
  const closeRequiresConfirm = sessionHasIncompleteInAppAssessment(
    activeSession.inAppAssessments ?? [],
    runner.stateByResultId,
  );

  const requestClose = () => {
    if (closeRequiresConfirm) {
      if (runner.selectedState.phase === 'running') {
        closePausedTimerRef.current = true;
        runner.pause();
      }
      setConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  const stayOpen = () => {
    setConfirmCloseOpen(false);
    if (closePausedTimerRef.current) {
      closePausedTimerRef.current = false;
      runner.start();
    }
  };

  const dismissRedoPrompt = () => {
    setRedoPromptOpen(false);
    setRedoReason('');
  };

  const selectKind = (kind: TimedContentKind) => {
    runner.pause();
    setWordListSwitchPrompt(null);
    dismissRedoPrompt();
    const nextStudents = studentsForKind(activeSession, kind);
    setSelectedKind(kind);
    setSelectedAssessmentResultId(firstAssessmentResultId(nextStudents));
  };

  const body = (
    <InAppAssessmentWindowBody
      groups={groups}
      needsPicker={needsPicker}
      selectedKind={selectedKind}
      students={students}
      selectedAssessmentResultId={selectedAssessmentResultId}
      runner={runner}
      navigationLocked={navigationLocked}
      timeUpClickMode={timeUpClickMode}
      wordListSwitchPrompt={wordListSwitchPrompt}
      redoPromptOpen={redoPromptOpen}
      redoReason={redoReason}
      redoPending={redoPending}
      onRedoReasonChange={setRedoReason}
      onRequestRedo={() => setRedoPromptOpen(true)}
      onCancelRedo={dismissRedoPrompt}
      onConfirmRedo={() => {
        if (!selectedAssessmentResultId || redoReason.trim().length === 0) {
          return;
        }
        redo(selectedAssessmentResultId, redoReason.trim());
      }}
      onStayAndCompleteWordList={() => {
        setWordListSwitchPrompt(null);
        const wordList = selectedStudent?.assessments.find(
          (assessment) => inAppProgressMonitorPart(assessment) === 'word-list',
        );
        if (wordList && wordList.assessmentResultId !== selectedAssessmentResultId) {
          runner.pause();
          dismissRedoPrompt();
          setSelectedAssessmentResultId(wordList.assessmentResultId);
        }
      }}
      onSwitchStudentAnyway={() => {
        const targetStudentId = wordListSwitchPrompt?.targetStudentId;
        setWordListSwitchPrompt(null);
        if (!targetStudentId) {
          return;
        }
        runner.pause();
        dismissRedoPrompt();
        const entry = students.find((row) => row.student.id === targetStudentId);
        setSelectedAssessmentResultId(entry?.assessments[0]?.assessmentResultId ?? null);
      }}
      onTimeUpClickModeChange={setTimeUpClickMode}
      onSubmitAssessment={submitAssessment}
      onSelectKind={selectKind}
      onSelectStudent={(studentId) => {
        if (navigationLocked || studentId === selectedStudent?.student.id) {
          return;
        }
        const blockedName = selectedStudent ? wordListSwitchBlockName(selectedStudent, runner.stateByResultId) : null;
        if (blockedName) {
          setWordListSwitchPrompt({ studentName: blockedName, targetStudentId: studentId });
          return;
        }
        setWordListSwitchPrompt(null);
        dismissRedoPrompt();
        runner.pause();
        const entry = students.find((row) => row.student.id === studentId);
        setSelectedAssessmentResultId(entry?.assessments[0]?.assessmentResultId ?? null);
      }}
      onSelectPmTab={(assessmentResultId) => {
        if (navigationLocked || assessmentResultId === selectedAssessmentResultId) {
          return;
        }
        setWordListSwitchPrompt(null);
        dismissRedoPrompt();
        runner.pause();
        setSelectedAssessmentResultId(assessmentResultId);
      }}
      onChangeContent={() => {
        if (navigationLocked) {
          return;
        }
        setWordListSwitchPrompt(null);
        dismissRedoPrompt();
        runner.pause();
        setSelectedKind(null);
      }}
    />
  );

  const frame = (
    <InAppAssessmentWindowFrame
      headerTitle={headerTitle}
      minimized={minimized}
      isPictureInPicture={isPictureInPicture}
      pictureInPictureSupported={pictureInPictureSupported}
      confirmCloseOpen={confirmCloseOpen}
      closeRequiresConfirm={closeRequiresConfirm}
      useDialogTitle={!isPictureInPicture}
      onContentElement={setContentElement}
      onToggleMinimized={() => setMinimized((current) => !current)}
      onEnterPictureInPicture={onEnterPictureInPicture}
      onRequestClose={requestClose}
      onStay={stayOpen}
      onCloseAnyway={() => {
        closePausedTimerRef.current = false;
        setConfirmCloseOpen(false);
        onClose();
      }}
    >
      {body}
    </InAppAssessmentWindowFrame>
  );

  useLayoutEffect(() => {
    if (!pipWindow) {
      return;
    }

    const root = createRoot(pipWindow.document.body);
    pipRootRef.current = root;

    return () => {
      try {
        root.unmount();
      } catch {
        // The PiP document may already be gone when the browser closes the window.
      }
      if (pipRootRef.current === root) {
        pipRootRef.current = null;
      }
    };
  }, [pipWindow]);

  useLayoutEffect(() => {
    if (!isPictureInPicture || !pipWindow || !pipRootRef.current) {
      return;
    }

    pipRootRef.current.render(
      <AssessmentWindowProvider
        ownerDocument={pipWindow.document}
        ownerWindow={pipWindow}
        portalContainer={pipWindow.document.body}
      >
        {frame}
      </AssessmentWindowProvider>,
    );
  });

  if (isPictureInPicture) {
    return null;
  }

  return (
    <DialogPrimitive.Root
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next) {
          requestClose();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <AssessmentWindowProvider ownerDocument={document} ownerWindow={window} portalContainer={contentElement}>
          {frame}
        </AssessmentWindowProvider>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function InAppAssessmentWindowFrame({
  headerTitle,
  minimized,
  isPictureInPicture,
  pictureInPictureSupported,
  confirmCloseOpen,
  closeRequiresConfirm,
  useDialogTitle,
  onContentElement,
  onToggleMinimized,
  onEnterPictureInPicture,
  onRequestClose,
  onStay,
  onCloseAnyway,
  children,
}: {
  headerTitle: string;
  minimized: boolean;
  isPictureInPicture: boolean;
  pictureInPictureSupported: boolean;
  confirmCloseOpen: boolean;
  closeRequiresConfirm: boolean;
  useDialogTitle: boolean;
  onContentElement: (element: HTMLDivElement | null) => void;
  onToggleMinimized: () => void;
  onEnterPictureInPicture: () => void;
  onRequestClose: () => void;
  onStay: () => void;
  onCloseAnyway: () => void;
  children: ReactNode;
}) {
  const Title = useDialogTitle ? DialogPrimitive.Title : 'h2';
  const shell = (
    <>
      <div className="flex items-center gap-2 bg-air-primary px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <Title className="truncate text-sm font-semibold">{headerTitle}</Title>
          {useDialogTitle ? (
            <DialogPrimitive.Description className="sr-only">In-app assessment</DialogPrimitive.Description>
          ) : (
            <p className="sr-only">In-app assessment</p>
          )}
        </div>
        {pictureInPictureSupported && !isPictureInPicture && (
          <button
            type="button"
            onClick={onEnterPictureInPicture}
            aria-label="Open assessment in picture-in-picture"
            className="rounded p-1 hover:bg-white/20"
            data-testid="in-app-assessment-enter-picture-in-picture"
          >
            <PictureInPicture2 className="h-4 w-4" aria-hidden />
          </button>
        )}
        {!isPictureInPicture && (
          <>
            <button
              type="button"
              onClick={onToggleMinimized}
              aria-label={minimized ? 'Expand assessment' : 'Minimize assessment'}
              className="rounded p-1 hover:bg-white/20"
              data-testid="in-app-assessment-minimize"
            >
              <Minus className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Close assessment"
              className="rounded p-1 hover:bg-white/20"
              data-testid="in-app-assessment-close"
              onClick={onRequestClose}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </>
        )}
      </div>

      <div
        className={cn('min-h-0 flex-1 flex-col overflow-hidden', !isPictureInPicture && minimized ? 'hidden' : 'flex')}
      >
        {children}
      </div>

      {confirmCloseOpen && <InAppAssessmentCloseConfirm onStay={onStay} onCloseAnyway={onCloseAnyway} />}
    </>
  );

  if (isPictureInPicture) {
    return (
      <div
        ref={onContentElement}
        className="relative flex h-full w-full flex-col overflow-hidden bg-background"
        data-testid="in-app-assessment-window"
      >
        {shell}
      </div>
    );
  }

  return (
    <DialogPrimitive.Content
      ref={onContentElement}
      onInteractOutside={(event) => event.preventDefault()}
      onEscapeKeyDown={(event) => {
        if (confirmCloseOpen) {
          event.preventDefault();
          onStay();
          return;
        }
        if (closeRequiresConfirm) {
          event.preventDefault();
          onRequestClose();
        }
      }}
      className={cn(
        'relative fixed bottom-4 right-4 z-50 flex w-[min(30rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl',
        minimized ? 'h-auto' : 'h-[min(40rem,calc(100vh-2rem))]',
      )}
      data-testid="in-app-assessment-window"
    >
      {shell}
    </DialogPrimitive.Content>
  );
}

type TimedContentKind = 'gla' | 'progress_monitor' | 'fluency_check';

const TIMED_CONTENT_LABEL: Record<TimedContentKind, string> = {
  gla: 'GLA',
  progress_monitor: 'Progress monitor',
  fluency_check: 'Fluency check',
};

interface TimedContentGroup {
  kind: TimedContentKind;
  label: string;
  requiresWaitingRoom: boolean;
  students: InAppAssessmentStudent[];
}

function timedContentKindFromFormat(format: AssessmentFormat): TimedContentKind | null {
  if (format === AssessmentFormat.grade_level_fluency_v2) {
    return 'gla';
  }
  if (format === AssessmentFormat.fluency_check) {
    return 'fluency_check';
  }
  if (isProgressMonitorFormat(format)) {
    return 'progress_monitor';
  }
  return null;
}

function compareTimedContentKinds(a: TimedContentKind, b: TimedContentKind): number {
  if (a === 'gla' && b !== 'gla') {
    return -1;
  }
  if (b === 'gla' && a !== 'gla') {
    return 1;
  }
  return TIMED_CONTENT_LABEL[a].localeCompare(TIMED_CONTENT_LABEL[b]);
}

type ProgressMonitorTabKind = 'passage' | 'word-list';

/**
 * Passage wins when `readingPassage` is non-blank, even if `words` is also set. Contents with
 * neither a passage nor a word list are skipped so they never become a tab.
 */
function progressMonitorTabKind(assessment: CoachPortalInAppAssessment): ProgressMonitorTabKind | null {
  if (assessment.readingPassage?.trim()) {
    return 'passage';
  }
  if (assessment.words.length > 0) {
    return 'word-list';
  }
  return null;
}

function progressMonitorTabLabel(kind: ProgressMonitorTabKind): string {
  return kind === 'passage' ? 'Passage' : 'Word list';
}

function orderedRunnablePmAssessments(assessments: CoachPortalInAppAssessment[]): CoachPortalInAppAssessment[] {
  const runnable = assessments.filter((assessment) => progressMonitorTabKind(assessment) != null);
  return [
    ...runnable.filter((assessment) => progressMonitorTabKind(assessment) === 'passage'),
    ...runnable.filter((assessment) => progressMonitorTabKind(assessment) === 'word-list'),
  ];
}

function tokensForAssessment(assessment: CoachPortalInAppAssessment | undefined): string[] {
  if (!assessment) {
    return [];
  }
  const passage = assessment.readingPassage?.trim();
  if (passage) {
    return tokenizePassage(passage);
  }
  return assessment.words;
}

function firstAssessmentResultId(students: InAppAssessmentStudent[]): string | null {
  return students[0]?.assessments[0]?.assessmentResultId ?? null;
}

function studentForResultId(
  students: InAppAssessmentStudent[],
  resultId: string | null,
): InAppAssessmentStudent | undefined {
  if (!resultId) {
    return undefined;
  }
  return students.find((entry) => entry.assessments.some((assessment) => assessment.assessmentResultId === resultId));
}

function studentsForKind(session: CoachPortalSession, kind: TimedContentKind): InAppAssessmentStudent[] {
  const assessments = session.inAppAssessments ?? [];

  return session.students
    .flatMap((student) => {
      const matching = assessments.filter(
        (row) => row.studentId === student.id && timedContentKindFromFormat(row.format) === kind,
      );
      const contents = kind === 'progress_monitor' ? orderedRunnablePmAssessments(matching) : matching;
      if (contents.length === 0) {
        return [];
      }
      return [
        {
          student,
          displayName: student.coachFacingDisplayName ?? '',
          gradeLabel: student.grade ? `${studentYearOption(student.grade)} grade` : null,
          assessments: contents,
        },
      ];
    })
    .toSorted((a, b) => a.displayName.localeCompare(b.displayName));
}

function timedContentGroups(session: CoachPortalSession): TimedContentGroup[] {
  const present = new Set<TimedContentKind>();
  for (const assessment of session.inAppAssessments ?? []) {
    const kind = timedContentKindFromFormat(assessment.format);
    if (kind) {
      present.add(kind);
    }
  }

  return [...present]
    .map((kind) => ({
      kind,
      label: TIMED_CONTENT_LABEL[kind],
      requiresWaitingRoom: kind === 'gla',
      students: studentsForKind(session, kind),
    }))
    .filter((group) => group.students.length > 0)
    .toSorted((a, b) => compareTimedContentKinds(a.kind, b.kind));
}

function InAppAssessmentWindowBody({
  groups,
  needsPicker,
  selectedKind,
  students,
  selectedAssessmentResultId,
  runner,
  navigationLocked,
  timeUpClickMode,
  wordListSwitchPrompt,
  redoPromptOpen,
  redoReason,
  redoPending,
  onRedoReasonChange,
  onRequestRedo,
  onCancelRedo,
  onConfirmRedo,
  onStayAndCompleteWordList,
  onSwitchStudentAnyway,
  onTimeUpClickModeChange,
  onSubmitAssessment,
  onSelectKind,
  onSelectStudent,
  onSelectPmTab,
  onChangeContent,
}: {
  groups: TimedContentGroup[];
  needsPicker: boolean;
  selectedKind: TimedContentKind | null;
  students: InAppAssessmentStudent[];
  selectedAssessmentResultId: string | null;
  runner: ReturnType<typeof useInAppAssessmentRunner>;
  navigationLocked: boolean;
  timeUpClickMode: TimeUpClickMode;
  wordListSwitchPrompt: { studentName: string; targetStudentId: string } | null;
  redoPromptOpen: boolean;
  redoReason: string;
  redoPending: boolean;
  onRedoReasonChange: (reason: string) => void;
  onRequestRedo: () => void;
  onCancelRedo: () => void;
  onConfirmRedo: () => void;
  onStayAndCompleteWordList: () => void;
  onSwitchStudentAnyway: () => void;
  onTimeUpClickModeChange: (mode: TimeUpClickMode) => void;
  onSubmitAssessment: (options: { finishedEarly: boolean }) => void;
  onSelectKind: (kind: TimedContentKind) => void;
  onSelectStudent: (studentId: string) => void;
  onSelectPmTab: (assessmentResultId: string) => void;
  onChangeContent: () => void;
}) {
  if (groups.length === 0) {
    return (
      <p className="p-6 text-sm text-muted-foreground">No assessments are assigned to the students in this class.</p>
    );
  }

  if (selectedKind == null) {
    return <InAppTimedContentPicker groups={groups} onSelect={onSelectKind} />;
  }

  const selectedGroup = groups.find((group) => group.kind === selectedKind);

  return (
    <InAppAssessmentPanel
      students={students}
      selectedKind={selectedKind}
      selectedAssessmentResultId={selectedAssessmentResultId}
      navigationLocked={navigationLocked}
      timeUpClickMode={timeUpClickMode}
      wordListSwitchPrompt={wordListSwitchPrompt}
      redoPromptOpen={redoPromptOpen}
      redoReason={redoReason}
      redoPending={redoPending}
      onRedoReasonChange={onRedoReasonChange}
      onRequestRedo={onRequestRedo}
      onCancelRedo={onCancelRedo}
      onConfirmRedo={onConfirmRedo}
      onStayAndCompleteWordList={onStayAndCompleteWordList}
      onSwitchStudentAnyway={onSwitchStudentAnyway}
      onTimeUpClickModeChange={onTimeUpClickModeChange}
      onSubmitAssessment={onSubmitAssessment}
      onSelectStudent={onSelectStudent}
      onSelectPmTab={onSelectPmTab}
      runner={runner}
      contentBar={
        needsPicker && selectedGroup
          ? {
              group: selectedGroup,
              onChangeContent,
            }
          : undefined
      }
    />
  );
}

function InAppTimedContentPicker({
  groups,
  onSelect,
}: {
  groups: TimedContentGroup[];
  onSelect: (kind: TimedContentKind) => void;
}) {
  const glaGroup = groups.find((group) => group.kind === 'gla');
  const glaCount = glaGroup?.students.length ?? 0;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4"
      data-testid="in-app-assessment-content-picker"
    >
      {glaGroup && (
        <div className="mb-4">
          <h2 className="text-base font-semibold">GLA Assessment Required</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {glaCount} {glaCount === 1 ? 'student needs' : 'students need'} to complete their GLA at the beginning of
            class. Please ensure GLA lessons are opened and displayed before class begins.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {groups.map((group) => (
          <InAppTimedContentCard key={group.kind} group={group} onSelect={() => onSelect(group.kind)} />
        ))}
      </div>
    </div>
  );
}

function InAppTimedContentCard({ group, onSelect }: { group: TimedContentGroup; onSelect: () => void }) {
  const studentCountLabel = `${group.students.length} ${group.students.length === 1 ? 'student' : 'students'}`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="rounded-xl border bg-background p-4 text-left transition-colors hover:bg-accent/40"
      data-testid={`in-app-assessment-content-${group.kind}`}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{group.label}</span>
            {group.requiresWaitingRoom && <WaitingRoomRequiredBadge />}
          </div>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground">
          {studentCountLabel}
          <ChevronRight className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {group.students.map((entry) => (
          <span
            key={entry.student.id}
            className={cn(
              'rounded-full px-2.5 py-0.5 text-sm',
              group.kind === 'gla' ? 'bg-blue-100 text-blue-800' : 'bg-muted text-foreground',
            )}
          >
            {entry.displayName}
          </span>
        ))}
      </div>
      {!group.requiresWaitingRoom && (
        <p className="mt-3 text-xs text-muted-foreground">
          No waiting room needed — students can remain in the classroom.
        </p>
      )}
    </button>
  );
}

function WaitingRoomRequiredBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
      <AlertTriangle className="h-3 w-3" />
      Waiting room required
    </span>
  );
}

function InAppAssessmentContentBar({
  group,
  onChangeContent,
  changeDisabled,
}: {
  group: TimedContentGroup;
  onChangeContent: () => void;
  changeDisabled: boolean;
}) {
  return (
    <div className="flex items-center gap-2 border-b px-4 py-2">
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Assessment type
      </span>
      {group.requiresWaitingRoom ? (
        <>
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
            <AlertTriangle className="h-3 w-3" />
            {group.label}
          </span>
          <span className="text-sm text-amber-700">· Waiting room required</span>
        </>
      ) : (
        <span className="text-sm font-semibold">{group.label}</span>
      )}
      <button
        type="button"
        onClick={onChangeContent}
        disabled={changeDisabled}
        className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        data-testid="in-app-assessment-change-content"
      >
        <ChevronLeft className="h-4 w-4" />
        Change
      </button>
    </div>
  );
}

function InAppAssessmentCloseConfirm({ onStay, onCloseAnyway }: { onStay: () => void; onCloseAnyway: () => void }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="in-app-assessment-close-confirm-title"
        className="w-full max-w-[22rem] rounded-2xl bg-background px-5 py-4 shadow-xl"
        data-testid="in-app-assessment-close-confirm"
      >
        <p id="in-app-assessment-close-confirm-title" className="flex items-start gap-2 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
          <span>Some students haven&apos;t completed the assessment. Close anyway?</span>
        </p>
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCloseAnyway}
            className="rounded px-2 py-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="in-app-assessment-close-confirm-button-close"
          >
            Yes, close
          </button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onStay}
            data-testid="in-app-assessment-close-confirm-button-cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

type PassageClickMode = 'markErrors' | 'pickLastWord' | 'disabled';

function clickModeForPhase(phase: AssessmentPhase, timeUpClickMode: TimeUpClickMode): PassageClickMode {
  switch (phase) {
    case 'running':
    case 'paused':
    case 'confirmingEarlyFinish':
    case 'earlyFinishReady':
      return 'markErrors';
    case 'timeUp':
      return timeUpClickMode;
    case 'idle':
    case 'submitting':
    case 'done':
      return 'disabled';
  }
}

type InAppAssessmentRunner = ReturnType<typeof useInAppAssessmentRunner>;

function InAppAssessmentPanel({
  students,
  selectedKind,
  selectedAssessmentResultId,
  navigationLocked,
  timeUpClickMode,
  wordListSwitchPrompt,
  redoPromptOpen,
  redoReason,
  redoPending,
  onRedoReasonChange,
  onRequestRedo,
  onCancelRedo,
  onConfirmRedo,
  onStayAndCompleteWordList,
  onSwitchStudentAnyway,
  onTimeUpClickModeChange,
  onSelectStudent,
  onSelectPmTab,
  onSubmitAssessment,
  runner,
  contentBar,
}: {
  students: InAppAssessmentStudent[];
  selectedKind: TimedContentKind;
  selectedAssessmentResultId: string | null;
  navigationLocked: boolean;
  timeUpClickMode: TimeUpClickMode;
  wordListSwitchPrompt: { studentName: string; targetStudentId: string } | null;
  redoPromptOpen: boolean;
  redoReason: string;
  redoPending: boolean;
  onRedoReasonChange: (reason: string) => void;
  onRequestRedo: () => void;
  onCancelRedo: () => void;
  onConfirmRedo: () => void;
  onStayAndCompleteWordList: () => void;
  onSwitchStudentAnyway: () => void;
  onTimeUpClickModeChange: (mode: TimeUpClickMode) => void;
  onSelectStudent: (studentId: string) => void;
  onSelectPmTab: (assessmentResultId: string) => void;
  onSubmitAssessment: (options: { finishedEarly: boolean }) => void;
  runner: InAppAssessmentRunner;
  contentBar?: { group: TimedContentGroup; onChangeContent: () => void };
}) {
  const {
    selectedState,
    stateByResultId,
    start,
    pause,
    reset,
    requestEarlyFinish,
    cancelEarlyFinish,
    confirmEarlyFinish,
    toggleMarkedWord,
    selectLastWord,
    setCanRetell,
  } = runner;

  const selectedStudent = studentForResultId(students, selectedAssessmentResultId);
  const selectedAssessment = selectedStudent?.assessments.find(
    (assessment) => assessment.assessmentResultId === selectedAssessmentResultId,
  );
  const words = tokensForAssessment(selectedAssessment);
  const showPmTabs = selectedKind === 'progress_monitor' && (selectedStudent?.assessments.length ?? 0) > 1;

  const { phase, markedWordIndexes, lastWordIndex, secondsRemaining, submittedStats, canRetell } = selectedState;

  const submitting = phase === 'submitting';
  const lastWord = lastWordIndex === null ? null : words[lastWordIndex] ?? null;
  const clickMode = clickModeForPhase(phase, timeUpClickMode);
  const finishedEarlyReady = phase === 'earlyFinishReady';
  const retellEligible = shouldShowInAppRetell(selectedAssessment, selectedStudent?.student.grade);
  const showRetell = retellEligible && (phase === 'timeUp' || finishedEarlyReady || submitting);
  const showSubmit =
    (phase === 'timeUp' || finishedEarlyReady || submitting) && (!retellEligible || canRetell !== null || submitting);
  const submitDisabled = submitting || (phase === 'timeUp' && lastWordIndex === null);
  const incorrectWordsCount =
    lastWordIndex === null
      ? markedWordIndexes.length
      : markedWordIndexes.filter((index) => index <= lastWordIndex).length;
  const passageAssessment = selectedStudent?.assessments.find(
    (assessment) => inAppProgressMonitorPart(assessment) === 'passage',
  );
  const passageSubmitted = passageAssessment
    ? isInAppAssessmentResultSubmitted(passageAssessment, stateByResultId[passageAssessment.assessmentResultId])
    : false;
  const showWordList = selectedAssessment != null && inAppProgressMonitorPart(selectedAssessment) === 'word-list';
  const passageText = showWordList ? undefined : selectedAssessment?.readingPassage?.trim();
  const lineBreaksBefore = passageText ? passageLineBreaksBefore(passageText) : undefined;
  const displayStats =
    phase === 'done' && submittedStats != null ? submittedStats : inAppAssessmentServerStats(selectedAssessment);
  const showingSavedResult =
    (phase === 'idle' || phase === 'done') && (displayStats != null || selectedAssessment?.assessmentFinished === true);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {contentBar && (
        <InAppAssessmentContentBar
          group={contentBar.group}
          onChangeContent={contentBar.onChangeContent}
          changeDisabled={navigationLocked}
        />
      )}
      <div className="px-4 pt-3">
        <InAppAssessmentStudentSelect
          students={students}
          selectedAssessmentResultId={selectedAssessmentResultId}
          disabled={navigationLocked}
          statusFor={(entry) => inAppStudentStatus(entry.assessments, stateByResultId)}
          onSelect={onSelectStudent}
        />
      </div>

      {wordListSwitchPrompt && (
        <WordListSwitchConfirm
          studentName={wordListSwitchPrompt.studentName}
          onStay={onStayAndCompleteWordList}
          onSwitchAnyway={onSwitchStudentAnyway}
        />
      )}

      {redoPromptOpen && showingSavedResult && (
        <InAppAssessmentRedoConfirm
          reason={redoReason}
          pending={redoPending}
          onReasonChange={onRedoReasonChange}
          onConfirm={onConfirmRedo}
          onCancel={onCancelRedo}
        />
      )}

      {showPmTabs && selectedStudent && (
        <ProgressMonitorTabs
          assessments={selectedStudent.assessments}
          selectedAssessmentResultId={selectedAssessmentResultId}
          disabled={navigationLocked}
          isTabDisabled={(assessment) =>
            inAppProgressMonitorPart(assessment) === 'word-list' && passageAssessment != null && !passageSubmitted
          }
          isFinished={(assessment) =>
            isInAppAssessmentResultSubmitted(assessment, stateByResultId[assessment.assessmentResultId])
          }
          onSelect={onSelectPmTab}
        />
      )}

      {showingSavedResult && (
        <InAppAssessmentCompleteBanner
          stats={displayStats}
          onRedo={onRequestRedo}
          wordListAssessmentResultId={
            selectedAssessment && progressMonitorTabKind(selectedAssessment) === 'passage'
              ? selectedStudent?.assessments.find((assessment) => progressMonitorTabKind(assessment) === 'word-list')
                  ?.assessmentResultId
              : undefined
          }
          onGoToWordList={onSelectPmTab}
        />
      )}

      {!showingSavedResult && (
        <div className="mt-3">
          <InAppAssessmentTimerBar
            phase={phase}
            secondsRemaining={secondsRemaining}
            onStart={start}
            onPause={pause}
            onReset={reset}
            onFinishedEarly={requestEarlyFinish}
            showSubmit={showSubmit}
            submitDisabled={submitDisabled}
            submitting={submitting}
            onSubmit={() => onSubmitAssessment({ finishedEarly: finishedEarlyReady })}
          />
        </div>
      )}

      {!showingSavedResult && phase === 'idle' && <InAppAssessmentHint />}
      {phase === 'running' && (
        <p className="flex items-center justify-center gap-1.5 px-4 pt-2 text-xs text-muted-foreground">
          <Lightbulb className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
          Click any word to mark errors.
        </p>
      )}
      {finishedEarlyReady && (
        <p className="flex items-center justify-center gap-1.5 px-4 pt-2 text-xs text-muted-foreground">
          <Lightbulb className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
          Please review the missed words before submitting the assessment.
        </p>
      )}
      {phase === 'confirmingEarlyFinish' && (
        <InAppAssessmentEarlyFinishConfirmBanner onConfirm={confirmEarlyFinish} onCancel={cancelEarlyFinish} />
      )}
      {phase === 'timeUp' && (
        <InAppAssessmentTimeUpClickMode mode={timeUpClickMode} onChange={onTimeUpClickModeChange} />
      )}
      {showRetell && <InAppRetellQuestion value={canRetell} onChange={setCanRetell} />}

      {!showingSavedResult && (
        <InAppAssessmentPassage
          words={words}
          markedWordIndexes={markedWordIndexes}
          lastWordIndex={lastWordIndex}
          clickMode={clickMode}
          boxed={showWordList}
          lineBreaksBefore={lineBreaksBefore}
          onWordClick={(wordIndex) =>
            clickMode === 'pickLastWord' ? selectLastWord(wordIndex) : toggleMarkedWord(wordIndex)
          }
        />
      )}

      <InAppAssessmentFooter
        incorrectWordsCount={
          showingSavedResult && phase === 'idle' && displayStats?.incorrectWordsCount != null
            ? displayStats.incorrectWordsCount
            : incorrectWordsCount
        }
        lastWord={showingSavedResult && phase === 'idle' ? null : lastWord}
        finishedEarly={
          finishedEarlyReady ||
          (submitting && lastWordIndex === null) ||
          (showingSavedResult && displayStats?.finishedEarly === true)
        }
      />
    </div>
  );
}

function InAppAssessmentRedoConfirm({
  reason,
  pending,
  onReasonChange,
  onConfirm,
  onCancel,
}: {
  reason: string;
  pending: boolean;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const canConfirm = reason.trim().length > 0 && !pending;

  return (
    <div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3" role="status">
      <div className="flex items-start gap-2 text-sm text-amber-950">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <p>Are you sure you want to redo it? Please provide a reason.</p>
          <input
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            placeholder="e.g. Student was distracted"
            disabled={pending}
            maxLength={500}
            className="mt-2 w-full rounded-md border border-amber-200 bg-white px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
            data-testid="in-app-assessment-redo-reason"
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-4 pl-6">
        <Button
          size="sm"
          className="bg-amber-500 text-white hover:bg-amber-600 disabled:bg-muted disabled:text-muted-foreground"
          onClick={onConfirm}
          disabled={!canConfirm}
          loading={pending}
          data-testid="in-app-assessment-redo-confirm"
        >
          Confirm redo
        </Button>
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          onClick={onCancel}
          disabled={pending}
          data-testid="in-app-assessment-redo-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function WordListSwitchConfirm({
  studentName,
  onStay,
  onSwitchAnyway,
}: {
  studentName: string;
  onStay: () => void;
  onSwitchAnyway: () => void;
}) {
  return (
    <div className="mx-4 mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3" role="status">
      <div className="flex items-start gap-2 text-sm text-amber-950">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
        <p>
          {studentName} still has a <span className="font-semibold">Word list</span> assessment pending. Both
          assessments must be completed.
        </p>
      </div>
      <div className="mt-3 flex items-center gap-4 pl-6">
        <Button size="sm" className="bg-amber-500 text-white hover:bg-amber-600" onClick={onStay}>
          Stay & complete
        </Button>
        <button type="button" className="text-sm text-muted-foreground hover:text-foreground" onClick={onSwitchAnyway}>
          Switch anyway
        </button>
      </div>
    </div>
  );
}

function InAppAssessmentHint() {
  return (
    <p className="flex items-center justify-center gap-1.5 px-4 pt-2 text-xs text-muted-foreground">
      <Lightbulb className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
      Start the timer, then click any word to mark errors.
    </p>
  );
}

function InAppAssessmentEarlyFinishConfirmBanner({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mx-4 mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-amber-900">Did the student finish the entire passage?</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={onConfirm}
              data-testid="in-app-assessment-confirm-early-finish"
            >
              Yes
            </Button>
            <Button variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InAppAssessmentCompleteBanner({
  stats,
  wordListAssessmentResultId,
  onGoToWordList,
  onRedo,
}: {
  stats: {
    totalWordsRead: number | null;
    incorrectWordsCount: number | null;
    totalSecondsSpent: number | null;
    finishedEarly: boolean | null;
  } | null;
  wordListAssessmentResultId: string | undefined;
  onGoToWordList: (assessmentResultId: string) => void;
  onRedo: () => void;
}) {
  const wcpm = stats ? calculateReadingWPM(stats) : null;

  return (
    <div
      className="mx-4 mt-3 rounded-xl border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800"
      data-testid="in-app-assessment-complete-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold underline decoration-green-700 underline-offset-4">Assessment complete</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRedo}
          className="h-8 border-slate-200 bg-white px-3 font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-700"
          data-testid="in-app-assessment-redo"
        >
          Redo
        </Button>
      </div>
      {stats && (
        <p className="mt-1 flex flex-wrap gap-x-4">
          {stats.totalSecondsSpent != null && (
            <span>
              Time: <strong>{stats.totalSecondsSpent}s</strong>
            </span>
          )}
          {stats.totalWordsRead != null && (
            <span>
              Words: <strong>{stats.totalWordsRead}</strong>
            </span>
          )}
          {stats.incorrectWordsCount != null && (
            <span>
              Errors: <strong>{stats.incorrectWordsCount}</strong>
            </span>
          )}
          {wcpm != null && (
            <span>
              WCPM: <strong>{Math.round(wcpm)}</strong>
            </span>
          )}
        </p>
      )}
      {wordListAssessmentResultId && (
        <div className="mt-3 flex justify-end border-t border-green-200 pt-3">
          <Button
            size="sm"
            className="bg-green-700 text-white hover:bg-green-800"
            onClick={() => onGoToWordList(wordListAssessmentResultId)}
            data-testid="in-app-assessment-go-to-word-list"
          >
            Go now →
          </Button>
        </div>
      )}
    </div>
  );
}

function InAppAssessmentTimerBar({
  phase,
  secondsRemaining,
  onStart,
  onPause,
  onReset,
  onFinishedEarly,
  showSubmit,
  submitDisabled,
  submitting,
  onSubmit,
}: {
  phase: AssessmentPhase;
  secondsRemaining: number;
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  onFinishedEarly: () => void;
  showSubmit: boolean;
  submitDisabled: boolean;
  submitting: boolean;
  onSubmit: () => void;
}) {
  const running = phase === 'running';
  const canStart = phase === 'idle' || phase === 'paused';
  const countdownUrgent = phase !== 'idle' && phase !== 'done' && secondsRemaining <= 5;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-y bg-muted/40 px-4 py-3">
      <div className="flex items-center gap-3">
        {running ? (
          <Button
            variant="ghost"
            size="sm"
            icon={Pause}
            onClick={onPause}
            className="text-muted-foreground"
            data-testid="in-app-assessment-pause"
          >
            Pause
          </Button>
        ) : (
          <Button
            variant="destructive"
            size="sm"
            icon={Play}
            onClick={onStart}
            disabled={!canStart}
            data-testid="in-app-assessment-start"
          >
            Start
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onReset}
          aria-label="Reset timer"
          className="h-8 w-8 text-muted-foreground"
          data-testid="in-app-assessment-reset"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      <span
        className={cn('text-center font-mono text-3xl font-bold tabular-nums', {
          'text-muted-foreground': phase === 'done',
          'text-red-600': countdownUrgent,
        })}
        data-testid="in-app-assessment-countdown"
        aria-live="off"
      >
        {formatCountdown(secondsRemaining)}
      </span>

      <div className="flex justify-end">
        {showSubmit ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={onSubmit}
            disabled={submitDisabled}
            loading={submitting}
            data-testid="in-app-assessment-submit"
          >
            Submit assessment
          </Button>
        ) : phase === 'running' ? (
          <Button
            variant="destructive"
            size="sm"
            icon={Flag}
            onClick={onFinishedEarly}
            data-testid="in-app-assessment-finished-early"
          >
            Finish early
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function InAppAssessmentTimeUpClickMode({
  mode,
  onChange,
}: {
  mode: TimeUpClickMode;
  onChange: (mode: TimeUpClickMode) => void;
}) {
  return (
    <div className="px-4 pt-2">
      <div className="flex flex-wrap items-center gap-2 text-xs" role="radiogroup" aria-label="Clicking words">
        <span className="text-muted-foreground">Clicking words:</span>
        <div className="inline-flex overflow-hidden rounded-md border">
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'markErrors'}
            onClick={() => onChange('markErrors')}
            className={cn(
              'px-2.5 py-1 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === 'markErrors'
                ? 'bg-air-primary text-white'
                : 'bg-white text-muted-foreground hover:text-foreground',
            )}
            data-testid="in-app-assessment-click-mode-mark-errors"
          >
            Mark errors
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'pickLastWord'}
            onClick={() => onChange('pickLastWord')}
            className={cn(
              'border-l px-2.5 py-1 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              mode === 'pickLastWord'
                ? 'border-air-primary bg-air-primary text-white'
                : 'bg-white text-muted-foreground hover:text-foreground',
            )}
            data-testid="in-app-assessment-click-mode-select-last-word"
          >
            Select last word
          </button>
        </div>
      </div>
      <p className="mt-2 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-600">
        <Lightbulb className="h-4 w-4 shrink-0" aria-hidden />
        Time&apos;s up! Click the last word that was read.
      </p>
    </div>
  );
}

/**
 * What a click on a word means, which depends on where the read is:
 * - `markErrors` while the student is reading, or after time is up in that mode
 * - `pickLastWord` after time is up, so the app can count words read through that token
 * - `disabled` before the timer starts and after submitting
 */
function InAppAssessmentPassage({
  words,
  markedWordIndexes,
  lastWordIndex,
  clickMode,
  boxed = false,
  lineBreaksBefore,
  onWordClick,
}: {
  words: string[];
  markedWordIndexes: number[];
  lastWordIndex: number | null;
  clickMode: PassageClickMode;
  /** Word lists use a scrolling grid of boxes. Passages stay inline text. */
  boxed?: boolean;
  /** Line breaks to render before each passage word. Ignored for the word-list grid. */
  lineBreaksBefore?: number[];
  onWordClick: (wordIndex: number) => void;
}) {
  const marked = new Set(markedWordIndexes);
  const interactive = clickMode !== 'disabled';
  const ariaLabel =
    clickMode === 'pickLastWord' ? 'Click the last word that was read' : 'Click a word to mark an error';

  const wordButtons = words.map((word, index) => {
    const isMarked = marked.has(index);
    const isLastWord = lastWordIndex === index;
    const canClick =
      interactive &&
      (clickMode === 'pickLastWord'
        ? canSelectInAppAssessmentLastWord(index, markedWordIndexes)
        : isMarked || canMarkInAppAssessmentError(index, lastWordIndex));

    const breaks = boxed ? 0 : lineBreaksBefore?.[index] ?? 0;

    return (
      <Fragment key={`${index}-${word}`}>
        {Array.from({ length: breaks }, (_, breakIndex) => (
          <br key={`break-${index}-${breakIndex}`} />
        ))}
        <button
          type="button"
          disabled={!canClick}
          onClick={() => onWordClick(index)}
          aria-pressed={clickMode === 'pickLastWord' ? isLastWord : isMarked}
          className={cn(
            'transition-colors',
            boxed
              ? 'flex h-14 items-center justify-center rounded-lg border px-1 text-base'
              : 'mr-[0.3em] rounded px-0.5 align-baseline',
            canClick ? 'cursor-pointer' : 'cursor-default',
            canClick && !boxed && 'hover:bg-accent',
            canClick && boxed && !isLastWord && 'hover:border-slate-300',
            !canClick && interactive && 'opacity-50',
            boxed && !isMarked && !isLastWord && 'border-slate-200 bg-slate-50 text-slate-900',
            !boxed &&
              isMarked &&
              !isLastWord &&
              'text-red-600 underline decoration-red-500 decoration-2 underline-offset-4',
            boxed &&
              isMarked &&
              !isLastWord &&
              'border-red-200 bg-white text-red-500 underline decoration-red-400 decoration-2 underline-offset-4',
            isLastWord && 'border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-900',
          )}
          data-testid={`in-app-assessment-word-${index}`}
        >
          {word}
        </button>
      </Fragment>
    );
  });

  if (boxed) {
    return (
      <div className="mx-4 mb-3 mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
        <p className="px-4 pt-3 text-sm italic text-slate-400">*Please read the words in horizontal order.</p>
        <div
          className="grid min-h-0 flex-1 grid-cols-6 content-start gap-2 overflow-y-auto px-3 pb-3 pt-2"
          data-testid="in-app-assessment-word-list"
          aria-label={ariaLabel}
        >
          {wordButtons}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-3 mt-3 flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        className="max-h-full overflow-y-auto rounded-xl border border-slate-200 bg-white px-4 py-3 text-lg leading-9"
        data-testid="in-app-assessment-passage"
        aria-label={ariaLabel}
      >
        {wordButtons}
      </div>
    </div>
  );
}

function InAppAssessmentFooter({
  incorrectWordsCount,
  lastWord,
  finishedEarly,
}: {
  incorrectWordsCount: number;
  lastWord: string | null;
  finishedEarly: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border-t px-4 py-3 text-sm">
      <span className="flex items-center gap-1.5">
        Wrong:
        <span
          className="rounded-md bg-red-50 px-2 py-0.5 font-semibold text-red-600"
          data-testid="in-app-assessment-wrong-count"
        >
          {incorrectWordsCount}
        </span>
      </span>
      {finishedEarly ? (
        <span
          className="rounded-md bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800"
          data-testid="in-app-assessment-last-word"
        >
          Full passage
        </span>
      ) : lastWord ? (
        <span className="flex items-center gap-1.5">
          Finish:
          <span
            className="rounded-md bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800"
            data-testid="in-app-assessment-last-word"
          >
            {lastWord}
          </span>
        </span>
      ) : (
        <span className="flex items-center gap-1.5">
          Finish:
          <span
            className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground"
            data-testid="in-app-assessment-last-word"
          >
            —
          </span>
        </span>
      )}
    </div>
  );
}

const AVATAR_PALETTE = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-cyan-100 text-cyan-700',
];

function avatarClassName(studentId: string): string {
  const seed = [...studentId].reduce((total, character) => total + character.charCodeAt(0), 0);
  return AVATAR_PALETTE[seed % AVATAR_PALETTE.length];
}

function StudentAvatar({ studentId, displayName }: { studentId: string; displayName: string }) {
  return (
    <span
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
        avatarClassName(studentId),
      )}
      aria-hidden
    >
      {displayName.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}

function InAppRetellQuestion({ value, onChange }: { value: boolean | null; onChange: (value: boolean) => void }) {
  return (
    <div className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
      <span className="text-sm">Is student able to retell?</span>
      <div className="flex items-center gap-4 text-sm">
        <label className="inline-flex items-center gap-1.5 text-green-700">
          <input
            type="radio"
            name="in-app-assessment-retell"
            className="accent-green-600"
            checked={value === true}
            onChange={() => onChange(true)}
            data-testid="in-app-assessment-retell-yes"
          />
          Yes
        </label>
        <label className="inline-flex items-center gap-1.5 text-red-600">
          <input
            type="radio"
            name="in-app-assessment-retell"
            className="accent-red-600"
            checked={value === false}
            onChange={() => onChange(false)}
            data-testid="in-app-assessment-retell-no"
          />
          No
        </label>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: InAppStudentStatus }) {
  switch (status) {
    case 'done':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Done
        </span>
      );
    case 'in_progress':
      return (
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">In progress</span>
      );
    case 'passage_done':
      return (
        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">Passage done</span>
      );
    case 'pending':
      return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Pending</span>;
  }
}

function StudentRow({
  entry,
  status,
  showStatus,
}: {
  entry: InAppAssessmentStudent;
  status: InAppStudentStatus;
  showStatus: boolean;
}) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <StudentAvatar studentId={entry.student.id} displayName={entry.displayName} />
      <span className="truncate font-semibold capitalize">{entry.displayName}</span>
      {entry.gradeLabel && (
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
          {entry.gradeLabel}
        </span>
      )}
      {showStatus && <StatusBadge status={status} />}
    </span>
  );
}

function ProgressMonitorTabs({
  assessments,
  selectedAssessmentResultId,
  disabled,
  isTabDisabled,
  isFinished,
  onSelect,
}: {
  assessments: CoachPortalInAppAssessment[];
  selectedAssessmentResultId: string | null;
  disabled: boolean;
  isTabDisabled: (assessment: CoachPortalInAppAssessment) => boolean;
  isFinished: (assessment: CoachPortalInAppAssessment) => boolean;
  onSelect: (assessmentResultId: string) => void;
}) {
  return (
    <div className="mt-2 flex justify-center gap-6 border-b px-4" role="tablist" aria-label="Progress monitor contents">
      {assessments.map((assessment) => {
        const kind = progressMonitorTabKind(assessment);
        if (!kind) {
          return null;
        }
        const selected = assessment.assessmentResultId === selectedAssessmentResultId;
        const finished = isFinished(assessment);
        const tabDisabled = disabled || isTabDisabled(assessment);
        return (
          <button
            key={assessment.assessmentResultId}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={tabDisabled}
            className={cn(
              '-mb-px inline-flex items-center gap-1 border-b-2 px-0 py-2 text-sm',
              selected
                ? 'border-foreground font-semibold text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
              tabDisabled && 'pointer-events-none opacity-50',
            )}
            onClick={() => onSelect(assessment.assessmentResultId)}
            data-testid={`in-app-assessment-pm-tab-${assessment.assessmentResultId}`}
          >
            {progressMonitorTabLabel(kind)}
            {finished && (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" aria-hidden />
                <span className="sr-only">completed</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function InAppAssessmentStudentSelect({
  students,
  selectedAssessmentResultId,
  disabled,
  statusFor,
  onSelect,
}: {
  students: InAppAssessmentStudent[];
  selectedAssessmentResultId: string | null;
  disabled: boolean;
  statusFor: (entry: InAppAssessmentStudent) => InAppStudentStatus;
  onSelect: (studentId: string) => void;
}) {
  const { portalContainer } = useAssessmentWindow();
  const selected = studentForResultId(students, selectedAssessmentResultId);

  return (
    <SelectPrimitive.Root value={selected?.student.id} onValueChange={onSelect} disabled={disabled}>
      <SelectPrimitive.Trigger
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring data-[state=open]:border-primary disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="in-app-assessment-student-select"
        aria-label="Select student"
      >
        {selected ? (
          <StudentRow entry={selected} status={statusFor(selected)} showStatus />
        ) : (
          <span className="text-muted-foreground">Select student</span>
        )}
        <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
          <span className="text-sm">{students.length}</span>
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4" />
          </SelectPrimitive.Icon>
        </span>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal container={portalContainer}>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-80 w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg"
        >
          <SelectPrimitive.Viewport>
            <SelectPrimitive.Group>
              <SelectPrimitive.Label className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Select student
              </SelectPrimitive.Label>
              {students.map((entry) => (
                <SelectPrimitive.Item
                  key={entry.student.id}
                  value={entry.student.id}
                  className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm outline-none data-[state=checked]:bg-accent/60 hover:bg-accent"
                  data-testid={`in-app-assessment-student-option-${entry.student.id}`}
                >
                  <SelectPrimitive.ItemText asChild>
                    <StudentRow entry={entry} status={statusFor(entry)} showStatus={false} />
                  </SelectPrimitive.ItemText>
                  <StatusBadge status={statusFor(entry)} />
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Group>
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
